<script lang="ts" setup>
import { onMounted, ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import ErrorState from "@/views/shared/components/ErrorState.vue";
import InviteUserModal from "@/components/InviteUserModal.vue";
import EditUserModal from "@/components/EditUserModal.vue";
import InviteOrgModal from "./components/InviteOrgModal.vue";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useSessionStore } from "@/stores/session-store";
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import type { IOrg, IUser } from "@/types";
import type { PartnerClient, PublisherProduct } from "@/types/project-types";
import type { Config } from "datatables.net";

const router = useRouter();
const route = useRoute();
const sessionStore = useSessionStore();
const { trackView } = useRecentlyViewed();
const baseUrl = `${import.meta.env.VITE_API_URL}/orgs`;

const orgId = computed(() => route.params.id as string);

const org = ref<IOrg | null>(null);
const orgUsers = ref<any[]>([]);
const partnerClients = ref<PartnerClient[]>([]);
const publisherProducts = ref<PublisherProduct[]>([]);
const isLoading = ref(false);
const isSaving = ref(false);
const isSavingCapabilities = ref(false);
const hasError = ref(false);
const errorType = ref<'connection' | 'http' | 'general'>('general');
const error = ref("");
const successMessage = ref("");

// User management state
const showInviteUserModal = ref(false);
const showEditUserModal = ref(false);
const editingUser = ref<IUser | null>(null);

// Partner management state
const showInviteOrgModal = ref(false);

// Delete confirmation modals
const showRemoveUserModal = ref(false);
const userToRemove = ref<any>(null);
const showRemovePartnerModal = ref(false);
const partnerToRemove = ref<PartnerClient | null>(null);

// Edit mode
const isEditMode = ref(false);
const editForm = ref({
  orgName: "",
  globalConcurrentWorkflowRunLimit: 5,
});

const hasChanges = computed(() => {
  if (!org.value) return false;
  return (
    editForm.value.orgName !== org.value.orgName ||
    editForm.value.globalConcurrentWorkflowRunLimit !==
      (org.value as any).globalConcurrentWorkflowRunLimit
  );
});

const isValidOrgName = computed(() => {
  return editForm.value.orgName.trim().length >= 3;
});

async function loadOrgData() {
  isLoading.value = true;
  hasError.value = false;
  error.value = "";
  try {
    // Use fetchWrapper like the list view does - it handles the streaming response automatically
    const allOrgs = (await fetchWrapper.get(baseUrl)) as any[];

    // Find the target organization
    const targetOrg = allOrgs.find((o: any) =>
      (o.orgId === orgId.value || o.OrgId === orgId.value)
    );

    if (!targetOrg) {
      throw new Error(`Organisation ${orgId.value} not found`);
    }

    org.value = {
      orgId: targetOrg.orgId || targetOrg.OrgId,
      orgName: targetOrg.orgName || targetOrg.OrgName,
      canPublishProducts: targetOrg.canPublishProducts ?? targetOrg.CanPublishProducts ?? false,
      canPartnerWithOrgs: targetOrg.canPartnerWithOrgs ?? targetOrg.CanPartnerWithOrgs ?? false,
      projectCount: targetOrg.projectCount ?? targetOrg.ProjectCount ?? 0,
    };

    // Load users for this org using admin endpoint
    try {
      const usersResponse = (await fetchWrapper.get(`${baseUrl}/${org.value.orgId}/users`)) as any[];
      orgUsers.value = usersResponse;
    } catch (error) {
      console.error("Failed to load users:", error);
      orgUsers.value = [];
    }

    // Load partner clients if enabled
    if (org.value.canPartnerWithOrgs) {
      try {
        const clientsResponse = await fetchWrapper.get(`${baseUrl}/${org.value.orgId}/partner-clients`) as any[];
        partnerClients.value = clientsResponse;
      } catch (error) {
        console.error("Failed to load partner clients:", error);
        partnerClients.value = [];
      }
    }

    // Load publisher products if enabled
    if (org.value.canPublishProducts) {
      try {
        const productsResponse = await fetchWrapper.get(`${baseUrl}/${org.value.orgId}/publisher-products`) as any[];
        publisherProducts.value = productsResponse;
      } catch (error) {
        console.error("Failed to load publisher products:", error);
        publisherProducts.value = [];
      }
    }

    // Initialize edit form
    editForm.value.orgName = org.value.orgName;
    editForm.value.globalConcurrentWorkflowRunLimit =
      (org.value as any).globalConcurrentWorkflowRunLimit || 5;

    // Track the organisation view
    trackView({
      id: org.value.orgId,
      name: org.value.orgName,
      type: "organisation",
      route: `/admin/organisations/${org.value.orgId}`,
      description: `${orgUsers.value.length} user${orgUsers.value.length !== 1 ? 's' : ''}, ${org.value.projectCount || 0} project${org.value.projectCount !== 1 ? 's' : ''}`,
      icon: "bi-building",
      metadata: {
        projectCount: org.value.projectCount,
        userCount: orgUsers.value.length,
        canPublishProducts: org.value.canPublishProducts,
        canPartnerWithOrgs: org.value.canPartnerWithOrgs,
      },
    });
  } catch (err) {
    console.error("Error loading org data:", err);
    hasError.value = true;
    errorType.value = 'connection'; // fetchWrapper already shows toast
    error.value =
      err instanceof Error
        ? err.message
        : "Failed to load organisation details";
  } finally {
    isLoading.value = false;
  }
}

function enableEditMode() {
  isEditMode.value = true;
  successMessage.value = "";
}

function cancelEdit() {
  if (org.value) {
    editForm.value.orgName = org.value.orgName;
    editForm.value.globalConcurrentWorkflowRunLimit =
      (org.value as any).globalConcurrentWorkflowRunLimit || 5;
  }
  isEditMode.value = false;
  successMessage.value = "";
}

async function saveChanges() {
  if (!isValidOrgName.value || !hasChanges.value) return;

  isSaving.value = true;
  error.value = "";
  successMessage.value = "";

  try {
    await fetchWrapper.put(`${baseUrl}/${org.value!.orgId}`, {
      orgName: editForm.value.orgName,
    });

    // Update local org data
    if (org.value) {
      org.value.orgName = editForm.value.orgName;
    }

    // If we just renamed the user's currently-active org, refresh the session so the UI reflects it
    if (sessionStore.session.currentOrgId === org.value!.orgId) {
      await sessionStore.syncSession();
    }

    successMessage.value = "Organisation updated successfully";
    isEditMode.value = false;

    // Reload data to ensure consistency
    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : "Failed to update organisation";
  } finally {
    isSaving.value = false;
  }
}

async function toggleCapability(capability: 'canPublishProducts' | 'canPartnerWithOrgs') {
  if (!org.value) return;

  const originalValue = org.value[capability];
  const newValue = !originalValue;

  isSavingCapabilities.value = true;
  error.value = "";

  try {
    const payload = {
      canPublishProducts: capability === 'canPublishProducts' ? newValue : org.value.canPublishProducts,
      canPartnerWithOrgs: capability === 'canPartnerWithOrgs' ? newValue : org.value.canPartnerWithOrgs,
    };

    const updated = await fetchWrapper.put(
      `${baseUrl}/${org.value.orgId}/capabilities`,
      payload
    );

    // Update local state with the response
    org.value.canPublishProducts = updated.canPublishProducts ?? updated.CanPublishProducts ?? false;
    org.value.canPartnerWithOrgs = updated.canPartnerWithOrgs ?? updated.CanPartnerWithOrgs ?? false;

    // If the updated org is the current user's org, refresh the session
    if (sessionStore.session.currentOrgId === org.value.orgId) {
      await sessionStore.syncSession();
    }

    successMessage.value = "Capabilities updated successfully";

    setTimeout(() => {
      successMessage.value = "";
    }, 3000);
  } catch (err) {
    console.error("Failed to update capabilities:", err);
    error.value =
      err instanceof Error ? err.message : "Failed to update capabilities";

    // Reload to revert UI state
    await loadOrgData();
  } finally {
    isSavingCapabilities.value = false;
  }
}

function openInviteUserModal() {
  showInviteUserModal.value = true;
}

async function handleUserInvited() {
  // Reload users after inviting
  await loadOrgData();
  showInviteUserModal.value = false;
}

function openEditUserModal(user: IUser) {
  editingUser.value = user;
  showEditUserModal.value = true;
}

async function handleUserUpdated() {
  // Reload users after update
  await loadOrgData();
  editingUser.value = null;
}

function removeUser(user: any) {
  userToRemove.value = user;
  showRemoveUserModal.value = true;
}

async function confirmRemoveUser() {
  if (!org.value || !userToRemove.value) return;

  try {
    isSaving.value = true;
    await fetchWrapper.delete(`${baseUrl}/${org.value.orgId}/users/${userToRemove.value.id}`);
    await loadOrgData();
    successMessage.value = "User removed successfully";
    setTimeout(() => { successMessage.value = ""; }, 3000);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "Failed to remove user";
  } finally {
    isSaving.value = false;
    showRemoveUserModal.value = false;
    userToRemove.value = null;
  }
}

function formatTimeAgo(timestamp: string) {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now.getTime() - activityTime.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

function openInviteOrgModal() {
  showInviteOrgModal.value = true;
}

async function handleOrgInvited() {
  // Reload org data to refresh partner clients list
  await loadOrgData();
  showInviteOrgModal.value = false;
}

function removePartnerClient(client: PartnerClient) {
  partnerToRemove.value = client;
  showRemovePartnerModal.value = true;
}

async function confirmRemovePartner() {
  if (!org.value || !partnerToRemove.value) return;
  const client = partnerToRemove.value;

  try {
    isSaving.value = true;
    const partnershipsUrl = `${import.meta.env.VITE_API_URL}/partnerships/${client.partnershipId}/deactivate`;
    await fetchWrapper.post(partnershipsUrl, {});
    await loadOrgData();
    const successText = client.isPending ? 'Invitation cancelled' : 'Partnership removed';
    successMessage.value = `${successText} for ${client.organizationName}`;
    setTimeout(() => { successMessage.value = ""; }, 3000);
  } catch (err) {
    error.value = err instanceof Error ? err.message : (client.isPending ? "Failed to cancel invitation" : "Failed to remove partnership");
  } finally {
    isSaving.value = false;
    showRemovePartnerModal.value = false;
    partnerToRemove.value = null;
  }
}

// Delete organisation
const showDeleteOrgModal = ref(false);

async function confirmDeleteOrg() {
  // TODO: Implement delete org API call
  showDeleteOrgModal.value = false;
}

// DataView table options
const usersTableOptions = computed((): Config => ({
  columns: [
    {
      data: null,
      title: 'Name',
      render: (_: any, __: string, row: any) =>
        row.isPending
          ? '<span class="text-muted fst-italic">(Pending)</span>'
          : `<strong>${row.firstName} ${row.lastName}</strong>`,
    },
    { data: 'email', title: 'Email' },
    {
      data: 'role',
      title: 'Role',
      render: (d: string) => `<span class="badge bg-secondary">${d || 'N/A'}</span>`,
    },
    {
      data: null,
      title: 'Status',
      render: (_: any, __: string, row: any) =>
        row.isPending
          ? '<span class="badge bg-warning">Invited</span>'
          : '<span class="badge bg-success">Active</span>',
    },
    {
      data: null,
      title: '',
      orderable: false,
      className: 'text-end',
      render: (_: any, __: string, row: any) => {
        if (row.isPending) return '<span class="text-muted small"><i class="bi bi-clock me-1"></i>Awaiting acceptance</span>';
        return `<div class="d-flex gap-1 justify-content-end">
          <button class="bb-btn-secondary-cta btn-sm text-nowrap" data-action="edit"><i class="bi bi-pencil me-1"></i>Edit</button>
          <button class="bb-btn-danger btn-sm text-nowrap" data-action="remove-user"><i class="bi bi-trash me-1"></i>Remove</button>
        </div>`;
      },
    },
  ],
  layout: { topEnd: '', bottomEnd: '', bottomStart: '' },
} as Config));

function handleUserRowClick(row: any, _: number, event: Event) {
  const target = event.target as HTMLElement;
  if (target.closest('[data-action="edit"]')) {
    openEditUserModal(row);
  } else if (target.closest('[data-action="remove-user"]')) {
    removeUser(row);
  }
}

const partnerClientsTableOptions = computed((): Config => ({
  columns: [
    {
      data: null,
      title: 'Organization',
      render: (_: any, __: string, row: any) => {
        let html = `<strong>${row.organizationName}</strong>`;
        if (row.isPending) html += '<div class="small text-muted"><i class="bi bi-clock-history me-1"></i>Awaiting acceptance</div>';
        return html;
      },
    },
    {
      data: 'contactEmail',
      title: 'Contact',
      render: (d: string) => d || 'N/A',
    },
    {
      data: 'status',
      title: 'Status',
      render: (d: string) => {
        const classMap: Record<string, string> = {
          active: 'bg-success', inactive: 'bg-secondary', pending: 'bg-warning',
        };
        return `<span class="badge ${classMap[d] || 'bg-secondary'}">${d}</span>`;
      },
    },
    {
      data: null,
      title: 'Projects',
      render: (_: any, __: string, row: any) => row.isPending ? '-' : String(row.projectCount),
    },
    {
      data: null,
      title: 'Success Rate',
      render: (_: any, __: string, row: any) => {
        if (row.isPending) return '-';
        const cls = row.successRate >= 90 ? 'text-success' : row.successRate >= 80 ? 'text-warning' : 'text-danger';
        return `<span class="${cls}">${row.successRate.toFixed(1)}%</span>`;
      },
    },
    {
      data: null,
      title: 'Last Activity',
      render: (_: any, __: string, row: any) =>
        row.isPending
          ? `<small class="text-muted">${row.joinedDate}</small>`
          : `<small class="text-muted">${formatTimeAgo(row.lastActivity)}</small>`,
    },
    {
      data: null,
      title: '',
      orderable: false,
      className: 'text-end',
      render: (_: any, __: string, row: any) => {
        const icon = row.isPending ? 'bi-x-lg' : 'bi-trash';
        const label = row.isPending ? 'Cancel' : 'Remove';
        return `<button class="bb-btn-danger btn-sm text-nowrap" data-action="remove-partner"><i class="bi ${icon} me-1"></i>${label}</button>`;
      },
    },
  ],
  layout: { topEnd: '', bottomEnd: '', bottomStart: '' },
} as Config));

function handlePartnerRowClick(row: PartnerClient, _: number, event: Event) {
  const target = event.target as HTMLElement;
  if (target.closest('[data-action="remove-partner"]')) {
    removePartnerClient(row);
  }
}

const publisherProductsTableOptions: Config = {
  columns: [
    { data: 'name', title: 'Product Name', render: (d: string) => `<strong>${d}</strong>` },
    { data: 'version', title: 'Version', render: (d: number) => `<code class="small">v${d}</code>` },
    {
      data: 'status',
      title: 'Status',
      render: (d: string) => {
        const classMap: Record<string, string> = {
          published: 'bg-success', draft: 'bg-warning',
          paused: 'bg-danger', archived: 'bg-secondary',
        };
        return `<span class="badge ${classMap[d] || 'bg-secondary'}">${d}</span>`;
      },
    },
    { data: 'subscriberCount', title: 'Subscribers' },
    {
      data: 'successRate',
      title: 'Success Rate',
      render: (d: number) => {
        const cls = d >= 90 ? 'text-success' : d >= 80 ? 'text-warning' : 'text-danger';
        return `<span class="${cls}">${d.toFixed(1)}%</span>`;
      },
    },
    {
      data: 'monthlyRevenue',
      title: 'Revenue',
      render: (d: number) => `<span class="text-success fw-semibold">$${d.toLocaleString()}/mo</span>`,
    },
  ],
  layout: { topEnd: '', bottomEnd: '', bottomStart: '' },
} as Config;

onMounted(async () => {
  await loadOrgData();
});
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        :title="org?.orgName || 'Loading...'"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Organisation Admin', href: '/admin/organisations' },
          { label: org?.orgName || '...' }
        ]"
      >
        <template #actions>
          <div v-if="!isEditMode" class="d-flex gap-2">
            <button class="bb-btn-secondary-cta" @click="enableEditMode">
              <i class="bi bi-pencil me-2"></i>Edit Organisation
            </button>
          </div>
          <div v-else class="d-flex gap-2">
            <button class="bb-btn-cancel" @click="cancelEdit" :disabled="isSaving">
              Cancel
            </button>
            <button class="bb-btn-primary-cta" @click="saveChanges" :disabled="!hasChanges || !isValidOrgName || isSaving">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-check-lg me-2"></i>
              {{ isSaving ? "Saving..." : "Save Changes" }}
            </button>
          </div>
        </template>
      </PageHeader>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error State -->
      <ErrorState
        v-else-if="hasError"
        :type="errorType"
        @retry="loadOrgData"
      />

      <!-- Error Alert (for non-loading errors) -->
      <div v-if="!isLoading && !hasError && error" class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ error }}
      </div>

      <!-- Success Alert -->
      <div v-if="successMessage" class="alert alert-success" role="alert">
        <i class="bi bi-check-circle me-2"></i>
        {{ successMessage }}
      </div>

      <!-- Organisation Details -->
      <div v-if="!isLoading && !hasError && org">
        <div class="row g-3">
            <!-- Left Column: Basic Information + Users -->
            <div class="col-12 col-lg-8">
              <!-- Basic Information Card -->
              <div class="card shadow-sm mb-3">
                <div class="card-body py-3">
                  <h2 class="card-title mb-3 fw-semibold">
                    <i class="bi bi-building me-2"></i>Basic Information
                  </h2>

                  <!-- View Mode -->
                  <div v-if="!isEditMode">
                    <div class="mb-2">
                      <label class="text-muted small fw-semibold d-block mb-1"
                        >Organisation Name</label
                      >
                      <div class="fw-semibold">{{ org.orgName }}</div>
                    </div>

                    <div class="mb-2">
                      <label class="text-muted small fw-semibold d-block mb-1"
                        >Consumption Type</label
                      >
                      <div>
                        <span
                          class="badge bg-info"
                          v-if="(org as any).consumptionType"
                        >
                          {{ (org as any).consumptionType }}
                        </span>
                        <span v-else class="text-muted">Not set</span>
                      </div>
                    </div>

                    <div class="mb-2">
                      <label class="text-muted small fw-semibold d-block mb-1"
                        >Global Concurrent Workflow Limit</label
                      >
                      <div class="fw-semibold">
                        {{ (org as any).globalConcurrentWorkflowRunLimit || 5 }}
                      </div>
                    </div>

                    <div class="mb-0">
                      <label class="text-muted small fw-semibold d-block mb-1">API Key</label>
                      <div class="d-flex align-items-center gap-2">
                        <code class="bg-body-tertiary p-2 rounded flex-grow-1 small"
                          >••••••••••••••••</code
                        >
                        <button class="bb-btn-secondary-cta btn-sm">
                          <i class="bi bi-eye me-1"></i>Show
                        </button>
                        <button class="bb-btn-secondary-cta btn-sm">
                          <i class="bi bi-clipboard me-1"></i>Copy
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Edit Mode -->
                  <div v-else>
                    <div class="mb-3">
                      <label for="editOrgName" class="form-label fw-semibold"
                        >Organisation Name
                        <span class="text-danger">*</span></label
                      >
                      <input
                        id="editOrgName"
                        v-model="editForm.orgName"
                        type="text"
                        class="form-control"
                        :class="{
                          'is-invalid': editForm.orgName.length > 0 && !isValidOrgName,
                          'is-valid': isValidOrgName,
                        }"
                        placeholder="Enter organisation name"
                      />
                      <div v-if="!isValidOrgName" class="invalid-feedback">
                        Organisation name must be at least 3 characters
                      </div>
                    </div>

                    <div class="mb-0">
                      <label
                        for="editConcurrency"
                        class="form-label fw-semibold"
                        >Global Concurrent Workflow Limit</label
                      >
                      <input
                        id="editConcurrency"
                        v-model.number="editForm.globalConcurrentWorkflowRunLimit"
                        type="number"
                        class="form-control"
                        min="1"
                        max="100"
                      />
                      <small class="text-muted"
                        >Maximum number of workflows that can run
                        simultaneously</small
                      >
                    </div>
                  </div>
                </div>
              </div>

              <!-- Users Card -->
              <div class="card shadow-sm mb-3">
                <div class="card-body py-3">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h2 class="card-title mb-0 fw-semibold">
                      <i class="bi bi-people me-2"></i>Users ({{ orgUsers.length }})
                    </h2>
                    <button
                      class="bb-btn-primary-cta btn-sm"
                      @click="openInviteUserModal"
                    >
                      <i class="bi bi-person-add me-2"></i>Invite User
                    </button>
                  </div>

                  <DataView
                    :data="orgUsers"
                    :view-mode="'table'"
                    :options="usersTableOptions"
                    @row-click="handleUserRowClick"
                  >
                    <template #empty-state>
                      <div class="text-center py-4 text-muted">
                        <i class="bi bi-people fs-1"></i>
                        <p class="mt-2 mb-0">No users in this organisation</p>
                      </div>
                    </template>
                  </DataView>
                </div>
              </div>

              <!-- Partner Clients Card (only show if canPartnerWithOrgs is enabled) -->
              <div v-if="org.canPartnerWithOrgs" class="card shadow-sm mb-3">
                <div class="card-body py-3">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h2 class="card-title mb-0 fw-semibold">
                      <i class="bi bi-diagram-3 me-2"></i>Partner Clients ({{ partnerClients.length }})
                    </h2>
                    <div class="d-flex gap-2">
                      <button
                        class="bb-btn-primary-cta btn-sm"
                        @click="openInviteOrgModal"
                      >
                        <i class="bi bi-plus-circle me-2"></i>Invite Partner Client
                      </button>
                      <router-link
                        to="/partners/clients"
                        class="bb-btn-secondary-cta btn-sm"
                      >
                        <i class="bi bi-arrow-right me-2"></i>View All
                      </router-link>
                    </div>
                  </div>

                  <DataView
                    :data="partnerClients.slice(0, 5)"
                    :view-mode="'table'"
                    :options="partnerClientsTableOptions"
                    @row-click="handlePartnerRowClick"
                  >
                    <template #empty-state>
                      <div class="text-center py-4 text-muted">
                        <i class="bi bi-diagram-3 fs-1"></i>
                        <p class="mt-2 mb-0">No partner clients yet</p>
                        <small class="text-muted">Invite organisations to partner with you</small>
                      </div>
                    </template>
                  </DataView>
                </div>
              </div>

              <!-- Publisher Products Card (only show if canPublishProducts is enabled) -->
              <div v-if="org.canPublishProducts" class="card shadow-sm">
                <div class="card-body py-3">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h2 class="card-title mb-0 fw-semibold">
                      <i class="bi bi-box-seam me-2"></i>Publisher Products ({{ publisherProducts.length }})
                    </h2>
                    <router-link
                      to="/publisher/products"
                      class="bb-btn-secondary-cta btn-sm"
                    >
                      <i class="bi bi-arrow-right me-2"></i>View All
                    </router-link>
                  </div>

                  <DataView
                    :data="publisherProducts.slice(0, 5)"
                    :view-mode="'table'"
                    :options="publisherProductsTableOptions"
                  >
                    <template #empty-state>
                      <div class="text-center py-4 text-muted">
                        <i class="bi bi-box-seam fs-1"></i>
                        <p class="mt-2 mb-0">No products published yet</p>
                        <small class="text-muted">Create and publish products to the marketplace</small>
                      </div>
                    </template>
                  </DataView>
                </div>
              </div>
            </div>

            <!-- Right Column: Statistics + Quick Actions -->
            <div class="col-12 col-lg-4">
              <!-- Stats Card -->
              <div class="card shadow-sm mb-3">
                <div class="card-body py-3">
                  <h2 class="card-title mb-3 fw-semibold">
                    <i class="bi bi-bar-chart me-2"></i>Statistics
                  </h2>

                  <div class="mb-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted small">Users</span>
                      <span class="fs-6 fw-bold text-primary">{{
                        orgUsers.length
                      }}</span>
                    </div>
                  </div>

                  <div class="mb-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted small">Projects</span>
                      <span class="fs-6 fw-bold text-success">{{
                        org.projectCount ?? 0
                      }}</span>
                    </div>
                  </div>

                  <div class="mb-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted small">Connections</span>
                      <span class="fs-6 fw-bold text-info">-</span>
                    </div>
                  </div>

                  <div v-if="org.canPartnerWithOrgs" class="mb-2">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted small">Partner Clients</span>
                      <span class="fs-6 fw-bold text-warning">{{
                        partnerClients.length
                      }}</span>
                    </div>
                  </div>

                  <div v-if="org.canPublishProducts" class="mb-0">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted small">Publisher Products</span>
                      <span class="fs-6 fw-bold text-info">{{
                        publisherProducts.length
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Capabilities Card -->
              <div class="card shadow-sm mb-3">
                <div class="card-body py-3">
                  <h2 class="card-title mb-3 fw-semibold">
                    <i class="bi bi-toggle-on me-2"></i>Capabilities
                  </h2>

                  <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="fw-semibold">Publish Products</div>
                        <small class="text-muted">Allow publishing to marketplace</small>
                      </div>
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          :id="`switch-publish-${org?.orgId}`"
                          :checked="org!.canPublishProducts"
                          @change="toggleCapability('canPublishProducts')"
                          :disabled="isSavingCapabilities"
                        />
                      </div>
                    </div>
                  </div>

                  <div class="mb-0">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <div class="fw-semibold">Partner with Orgs</div>
                        <small class="text-muted">Enable partnership features</small>
                      </div>
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          role="switch"
                          :id="`switch-partner-${org?.orgId}`"
                          :checked="org!.canPartnerWithOrgs"
                          @change="toggleCapability('canPartnerWithOrgs')"
                          :disabled="isSavingCapabilities"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Quick Actions Card -->
              <div class="card shadow-sm">
                <div class="card-body py-3">
                  <h2 class="card-title mb-3 fw-semibold">
                    <i class="bi bi-lightning me-2"></i>Quick Actions
                  </h2>

                  <div class="d-grid gap-2">
                    <button class="bb-btn-secondary-cta btn-sm" @click="openInviteUserModal">
                      <i class="bi bi-person-add me-2"></i>Invite User
                    </button>
                    <button class="bb-btn-secondary-cta btn-sm">
                      <i class="bi bi-key me-2"></i>Regenerate API Key
                    </button>
                    <button class="bb-btn-danger btn-sm" @click="showDeleteOrgModal = true">
                      <i class="bi bi-trash me-2"></i>Delete Organisation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </PanelCard>

    <!-- Modals -->
    <InviteUserModal
      :show="showInviteUserModal"
      :orgId="org?.orgId"
      @update:show="showInviteUserModal = $event"
      @invited="handleUserInvited"
    />

    <EditUserModal
      v-model:show="showEditUserModal"
      :user="editingUser"
      :orgId="org?.orgId"
      @userUpdated="handleUserUpdated"
    />

    <InviteOrgModal
      :show="showInviteOrgModal"
      @update:show="showInviteOrgModal = $event"
      @invited="handleOrgInvited"
      :currentOrgId="org?.orgId"
    />
  </PanelGrid>

  <!-- Remove User Modal -->
  <div
    class="modal fade"
    :class="{ show: showRemoveUserModal }"
    :style="{ display: showRemoveUserModal ? 'block' : 'none' }"
    @click.self="showRemoveUserModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Remove User
          </h2>
          <button type="button" class="btn-close" @click="showRemoveUserModal = false"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to remove <strong>{{ userToRemove?.firstName }} {{ userToRemove?.lastName }}</strong> from this organisation?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="showRemoveUserModal = false">Cancel</button>
          <button class="btn btn-danger" @click="confirmRemoveUser" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            Remove
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showRemoveUserModal" class="modal-backdrop fade show" @click="showRemoveUserModal = false"></div>

  <!-- Remove / Cancel Partner Modal -->
  <div
    class="modal fade"
    :class="{ show: showRemovePartnerModal }"
    :style="{ display: showRemovePartnerModal ? 'block' : 'none' }"
    @click.self="showRemovePartnerModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ partnerToRemove?.isPending ? 'Cancel Invitation' : 'Remove Partnership' }}
          </h2>
          <button type="button" class="btn-close" @click="showRemovePartnerModal = false"></button>
        </div>
        <div class="modal-body">
          <p v-if="partnerToRemove?.isPending">Are you sure you want to cancel the invitation to <strong>{{ partnerToRemove?.organizationName }}</strong>?</p>
          <p v-else>Are you sure you want to remove the partnership with <strong>{{ partnerToRemove?.organizationName }}</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="showRemovePartnerModal = false">Cancel</button>
          <button class="btn btn-danger" @click="confirmRemovePartner" :disabled="isSaving">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            {{ partnerToRemove?.isPending ? 'Cancel Invitation' : 'Remove' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showRemovePartnerModal" class="modal-backdrop fade show" @click="showRemovePartnerModal = false"></div>

  <!-- Delete Organisation Modal -->
  <div
    class="modal fade"
    :class="{ show: showDeleteOrgModal }"
    :style="{ display: showDeleteOrgModal ? 'block' : 'none' }"
    @click.self="showDeleteOrgModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title text-danger">
            <i class="bi bi-exclamation-triangle me-2"></i>Delete Organisation
          </h2>
          <button type="button" class="btn-close" @click="showDeleteOrgModal = false"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete <strong>{{ org?.orgName }}</strong>?</p>
          <p class="text-muted mb-0">This action cannot be undone.</p>
        </div>
        <div class="modal-footer">
          <button class="bb-btn-cancel" @click="showDeleteOrgModal = false">Cancel</button>
          <button class="btn btn-danger" @click="confirmDeleteOrg">Delete</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteOrgModal" class="modal-backdrop fade show" @click="showDeleteOrgModal = false"></div>
</template>

<style scoped lang="scss">
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.section-title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--bs-body-color);
}

/* COMMENTED OUT: Global Bootstrap table overrides - should be in global styles.scss */
/*.table th {*/
/*  font-weight: 600;*/
/*  color: var(--bs-body-color);*/
/*  font-size: 0.875rem;*/
/*  border-bottom: 2px solid var(--bs-border-color);*/
/*}*/

/*.table td {*/
/*  vertical-align: middle;*/
/*  font-size: 0.875rem;*/
/*}*/

/*.table-sm td,*/
/*.table-sm th {*/
/*  padding: 0.5rem;*/
/*}*/

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.05);
}

code {
  font-size: 0.875rem;
}

.card-title {
  font-weight: 600;
}

/* Reduce excessive white space */
hr {
  margin: 1.5rem 0;
}

/* COMMENTED OUT: Global Bootstrap form overrides - should be in global styles.scss */
/*.form-check-input {*/
/*  width: 3em;*/
/*  height: 1.5em;*/
/*  cursor: pointer;*/
/*}*/

/*.form-check-input:disabled {*/
/*  cursor: not-allowed;*/
/*  opacity: 0.5;*/
/*}*/
</style>

<style lang="scss">
/* Override PanelGrid's height:100% for this view */
//.panel-grid .row .col .card {
//  height: auto !important;
//}
</style>
